import { useQuery, useMutation } from "@apollo/react-hooks";

import {
  GetShippingZoneDetail,
  GetShippingDetail,
  GetShippingRuleDetail
} from "../queries";
import {
  editShippingZone,
  deleteShippingZone,
  createShippingRule,
  editShippingRule,
  deleteShippingRule
} from "../mutation";
import { useState, useEffect } from "react";
import useInputState from "../../../../hooks/useInputState";
import usePreviousValue from "../../../../hooks/usePreviousValue";
import { useHistory } from "react-router-dom";
import { parseNetworkError } from "../helpers";
import useMaskedInputState from "../../../../hooks/useMaskedInputState";

export const useShippingZoneDetail = (id) => {
  const { data, loading } = useQuery(GetShippingZoneDetail, {
    variables: { id },
    skip: !id
  });

  const shippingZone = data && data.shippingZone;

  return {
    loading,
    shippingZone
  };
};

export const useChangeShippingZone = ({ zoneId, shippingZone }) => {
  const [stateIds, setStateIds] = useState([]);
  const [error, setError] = useState();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [ruleId, setRuleId] = useState("");
  const [searchText, setSearchText] = useInputState("");
  const [zoneName, setZoneName] = useInputState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isRestOfWorld, setIsRestOfWorld] = useState(false);
  const previousZoneName = usePreviousValue(shippingZone && shippingZone.name);
  const previousROW = usePreviousValue(
    shippingZone && shippingZone.isRestOfWorld
  );

  const onToggleRestOfWorld = () => {
    !isRestOfWorld && setStateIds([]);
    setIsRestOfWorld((state) => !state);
  };

  useEffect(() => {
    stateIds.length && setIsRestOfWorld(false);
  }, [stateIds]);

  useEffect(() => {
    if (!!shippingZone) {
      const { name, states, isRestOfWorld } = shippingZone;
      setZoneName(name);
      setIsRestOfWorld(isRestOfWorld);
      setStateIds(states.map((elem) => elem.id));
    }
  }, [shippingZone]);

  const [updateShippingZone] = useMutation(editShippingZone, {
    variables: {
      shippingZone: {
        id: zoneId,
        name: zoneName,
        stateIds,
        isRestOfWorld
      }
    },
    refetchQueries: [
      {
        query: GetShippingZoneDetail,
        variables: {
          id: zoneId
        }
      }
    ]
  });

  const onUpdateShippingZone = async () => {
    setIsSaving(true);
    setError();
    try {
      await updateShippingZone();
      setIsSaving(false);
    } catch (e) {
      setError(parseNetworkError(e));
      setIsSaving(false);
    }
  };

  const onAddShippingRule = () => {
    setIsModalOpen(true);
    setIsEdit(false);
  };

  const onEditShippingRule = (id) => {
    setIsModalOpen(true);
    setIsEdit(true);
    setRuleId(id);
  };

  const originalStates =
    shippingZone && shippingZone.states.map((elem) => elem.id);
  const formIsComplete = zoneName && (stateIds.length || isRestOfWorld);
  const isSubmitEnabled =
    formIsComplete &&
    (JSON.stringify(stateIds) !== JSON.stringify(originalStates) ||
      previousZoneName !== zoneName ||
      previousROW !== isRestOfWorld);

  return {
    stateIds,
    setStateIds,
    searchText,
    setSearchText,
    zoneName,
    setZoneName,
    onUpdateShippingZone,
    isSubmitDisabled: !isSubmitEnabled,
    isSaving,
    onToggleRestOfWorld,
    isRestOfWorld,
    error,
    onAddShippingRule,
    onEditShippingRule,
    isModalOpen,
    setIsModalOpen,
    isEdit,
    ruleId
  };
};

export const useRemoveShippingZone = (zoneId, profileId) => {
  const [isAlertOpen, setIsAlertOpen] = useState(false);

  const history = useHistory();

  const [removeShippingZone] = useMutation(deleteShippingZone, {
    refetchQueries: [{ query: GetShippingDetail, variables: { id: profileId } }]
  });

  const onRemoveConfirmed = async () => {
    try {
      await removeShippingZone({
        variables: {
          id: zoneId
        }
      });
      reset();
      history.push(`/settings/shipping/${profileId}`);
    } catch (err) {
      reset();
    }
  };

  const reset = () => {
    setIsAlertOpen(false);
  };

  return {
    isAlertOpen,
    onRemoveConfirmed,
    setIsAlertOpen
  };
};

export const useShippingRateDialog = ({
  setIsModalOpen,
  isEdit,
  ruleId,
  zoneId,
  shippingRule,
  loading
}) => {
  const floatRegex = /^(\d+)?([.]?\d{0,2})?$/;
  const [rate, setRate] = useMaskedInputState("", { regex: floatRegex });
  const [hasConditions, setHasConditions] = useState(false);
  const [condition, setCondition] = useInputState("");
  const [min, setMin] = useMaskedInputState("", { regex: floatRegex });
  const [max, setMax] = useMaskedInputState("", { regex: floatRegex });
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState();

  const onToggleCondition = () => {
    if (hasConditions) {
      setCondition("");
      setMin("");
      setMax("");
    }
    setHasConditions((state) => !state);
  };

  useEffect(() => {
    if (isEdit && shippingRule && !loading) {
      const { rate, min, max, condition } = shippingRule;
      setHasConditions(!!condition);
      setRate(rate.toString());
      condition && setCondition(condition);
      min && setMin(min.toString());
      max && setMax(max.toString());
    }
  }, [shippingRule, isEdit, loading]);

  const [addShippingRule] = useMutation(createShippingRule, {
    refetchQueries: [
      { query: GetShippingZoneDetail, variables: { id: zoneId } }
    ]
  });

  const [updateShippingRule] = useMutation(editShippingRule, {
    refetchQueries: [
      { query: GetShippingZoneDetail, variables: { id: zoneId } }
    ]
  });

  const [removeShippingRule] = useMutation(deleteShippingRule, {
    refetchQueries: [
      { query: GetShippingZoneDetail, variables: { id: zoneId } }
    ]
  });

  const onRemove = async () => {
    setIsSaving(true);
    setError();
    try {
      await removeShippingRule({
        variables: {
          id: ruleId
        }
      });
      onDismissModal();
    } catch (err) {
      setError(parseNetworkError(err));
      setIsSaving(false);
    }
  };

  const onSave = async () => {
    setIsSaving(true);
    setError();
    try {
      if (isEdit) {
        await updateShippingRule({
          variables: {
            shippingRule: {
              id: ruleId,
              ...(condition && { condition }),
              min: min ? parseFloat(min) : 0,
              max: max ? parseFloat(max) : 0,
              rate: parseFloat(rate)
            }
          }
        });
      } else {
        await addShippingRule({
          variables: {
            shippingRule: {
              shippingZoneId: zoneId,
              ...(condition && { condition }),
              min: min ? parseFloat(min) : 0,
              max: max ? parseFloat(max) : 0,
              rate: parseFloat(rate)
            }
          }
        });
      }
      onDismissModal();
    } catch (err) {
      setError(parseNetworkError(err));
      setIsSaving(false);
    }
  };

  const onDismissModal = () => {
    setIsModalOpen(false);
    setHasConditions(false);
    setIsSaving(false);
    setRate("");
    setCondition("");
    setMin("");
    setMax("");
    setError();
  };

  const conditionsFulfilled = hasConditions ? condition && (min || max) : true;
  const minMaxValid =
    hasConditions && min && max ? parseFloat(min) < parseFloat(max) : true;

  const isSubmitEnabled = rate && conditionsFulfilled && minMaxValid;

  return {
    rate,
    setRate,
    hasConditions,
    onToggleCondition,
    condition,
    setCondition,
    min,
    setMin,
    max,
    setMax,
    onDismissModal,
    onSave,
    isSubmitEnabled,
    minMaxValid,
    isSaving,
    error,
    onRemove
  };
};

export const useShippingRuleDetail = (id, isModalOpen, isEdit) => {
  const { data, loading } = useQuery(GetShippingRuleDetail, {
    variables: { id },
    skip: !id || !isModalOpen || !isEdit
  });

  const shippingRule = data && data.shippingRule;

  return {
    loading,
    shippingRule
  };
};
